import { SagaIterator } from 'redux-saga';
import { call, put } from 'redux-saga/effects';
import {
    ListProjectEnvironmentTypesResponse,
    listManyProjectEnvironmentTypes,
} from '../../../data/services/resource-manager/project-environment-type';
import {
    ClientError,
    FailureOperation,
    FailureResponse,
    SuccessResponse,
    isFailureResponse,
    isSuccessResponse,
} from '../../../models/common';
import { ProjectEnvironmentTypeResource } from '../../../models/project-environment-type';
import { KeyValuePair } from '../../../types/key-value-pair';
import { getValue } from '../../../utilities/key-value-pair';
import { getAccessToken } from '../../actions/identity/identity-action-creators';
import { GetAccessTokenForAzureResourceManagerPayload } from '../../actions/identity/identity-actions';
import {
    listProjectEnvironmentTypes,
    listProjectEnvironmentTypesError,
    listProjectEnvironmentTypesFailed,
    listProjectEnvironmentTypesSuccess,
} from '../../actions/project-environment-type/project-environment-type-action-creators';
import { ListProjectEnvironmentTypesAction } from '../../actions/project-environment-type/project-environment-type-actions';
import { createSagaError } from '../../effects/create-saga-error';
import { putAndAwait } from '../../effects/put-and-await';
import { rejectAction } from '../../effects/reject-action';
import { resolveAction } from '../../effects/resolve-action';
import { takeEvery } from '../../effects/take';
import { getActionsInGroup } from '../../utilities/groupable-action';

export function* listProjectEnvironmentTypesSaga(action: ListProjectEnvironmentTypesAction): SagaIterator {
    const { meta } = action;
    const { activityId } = meta ?? {};
    const actions = getActionsInGroup(action);
    const ids = actions.map((action) => action.payload.id);

    try {
        const accessToken: string = yield putAndAwait(
            getAccessToken(GetAccessTokenForAzureResourceManagerPayload(), meta)
        );

        const responses: KeyValuePair<string, ListProjectEnvironmentTypesResponse>[] = yield call(
            listManyProjectEnvironmentTypes,
            ids,
            accessToken,
            activityId
        );

        const failures = responses.filter((pair) => isFailureResponse(getValue(pair)));
        const successes = responses.filter((pair) => isSuccessResponse(getValue(pair)));

        if (failures.length > 0) {
            const payloads = failures.map((pair) => {
                const [id, failure] = pair as KeyValuePair<string, FailureResponse>;
                return { failure, id };
            });

            yield put(listProjectEnvironmentTypesFailed(payloads, meta));
        }

        if (successes.length > 0) {
            const payloads = successes.map((pair) => {
                const [id, response] = pair as KeyValuePair<string, SuccessResponse<ProjectEnvironmentTypeResource[]>>;
                const { data: result } = response;
                return { id, result };
            });

            yield put(listProjectEnvironmentTypesSuccess(payloads, meta));
        }

        yield resolveAction(action, responses.map(getValue));
    } catch (err) {
        const error: ClientError = yield createSagaError(err, FailureOperation.ListProjectEnvironmentTypes);
        const payloads = ids.map((id) => ({ error, id }));
        yield put(listProjectEnvironmentTypesError(payloads, meta));
        yield rejectAction(action, error);
    }
}

export function* listProjectEnvironmentTypesListenerSaga(): SagaIterator {
    yield takeEvery(listProjectEnvironmentTypes, listProjectEnvironmentTypesSaga);
}
